import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/auth';

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const getErrorMessage = (error: AxiosError<{ error?: string; message?: string }>): string => {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    return 'Unable to connect to server. Please check your internet connection.';
  }

  const status = error.response.status;
  const serverMessage = error.response.data?.error || error.response.data?.message;

  switch (status) {
    case 400:
      return serverMessage || 'Invalid request. Please check your input.';
    case 401:
      return serverMessage || 'Invalid email or password.';
    case 403:
      if (serverMessage?.includes('not approved')) {
        return 'Your account is pending approval. Please wait for admin confirmation.';
      }
      return 'Access denied. You do not have permission to perform this action.';
    case 404:
      return serverMessage || 'Resource not found.';
    case 409:
      return serverMessage || 'This record already exists.';
    case 422:
      return serverMessage || 'Please check the form for errors.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return serverMessage || 'An unexpected error occurred. Please try again.';
  }
};

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    // Auto-logout if account is suspended/rejected while logged in
    if (error.response?.status === 403) {
      const code = (error.response.data as any)?.code;
      if (code === 'ACCOUNT_NOT_ACTIVE' && !originalRequest?.url?.includes('/auth/')) {
        useAuthStore.getState().logout();
        window.location.href = '/login?reason=suspended';
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't retry for login requests
      if (originalRequest.url?.includes('/auth/affiliate/login')) {
        const friendlyError = new Error(getErrorMessage(error as AxiosError<{ error?: string; message?: string }>));
        return Promise.reject(friendlyError);
      }

      originalRequest._retry = true;

      try {
        await useAuthStore.getState().refreshAuth();
        const { accessToken } = useAuthStore.getState();
        if (accessToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    const friendlyError = new Error(getErrorMessage(error as AxiosError<{ error?: string; message?: string }>));
    (friendlyError as any).originalError = error;
    (friendlyError as any).status = error.response?.status;
    return Promise.reject(friendlyError);
  }
);

export default api;
